<template>
  <ModalLayout v-loading="pageLoading" icon="add-user" title="New Client" @close="close">
    <!-- form -->
    <ElForm ref="form" :model="form" :rules="rules" :disabled="loading" @submit.prevent.native label-position="top" label-width="100px">
      <ElFormItem prop="name" :error="errors.name">
        <span slot="label"> Client name </span>
        <ElInput v-model="form.name" placeholder="Ex. Adidas" :maxlength="50" minlength="3" show-word-limit />
      </ElFormItem>
      <ElFormItem prop="email" :error="errors.email">
        <span slot="label"> Client E-mail </span>
        <ElInput v-model="form.email" placeholder="Ex. johnstone@adidas.ro" />
      </ElFormItem>
      <ElFormItem
        v-if="isAgencyAdmin || isAgencyEmployee"
        prop="country"
        :error="errors.country"
      >
        <span slot="label"> Country </span>
        <ElSelect v-model="form.country" class="w-full" placeholder="Select country" filterable>
          <ElOption v-for="(country, index) in countries" :key="index" :label="country" :value="index" />
        </ElSelect>
      </ElFormItem>
      <ElFormItem
        v-if="isAgencyAdmin || isAgencyEmployee"
        prop="industry"
        :error="errors.industry"
      >
        <span slot="label"> Industry </span>
        <ElSelect v-model="form.industry" class="w-full" placeholder="Select industry" filterable>
          <ElOption v-for="(industry, index) in industries" :key="index" :label="industry" :value="index" />
        </ElSelect>
      </ElFormItem>
      <div>
        <div class="font-bold pb-0.5">Influencer Campaigns</div>
        <p class="text-sm">Set limits for the maximum number of active campaigns and influencers for this client’s influencer campaigns.</p>
        <ElFormItem prop="max_active_campaigns" :error="errors.max_active_campaigns">
          <span slot="label"> Max active campaigns ({{ resources.max_active_campaigns - form.max_active_campaigns }} available) </span>
          <ElInputNumber v-model="form.max_active_campaigns" :min="0" :max="resources.max_active_campaigns" class="w-full" />
        </ElFormItem>
        <ElFormItem prop="max_monthly_influencers" :error="errors.max_monthly_influencers">
          <span slot="label"> Influencers ({{ resources.max_monthly_influencers - form.max_monthly_influencers }} available) </span>
          <ElInputNumber v-model="form.max_monthly_influencers" :min="0" :max="resources.max_monthly_influencers" class="w-full" />
        </ElFormItem>
      </div>
      <div>
        <div class="font-bold pb-0.5">Social Campaigns</div>
        <div v-for="publisher in publishers" :key="publisher.id">
          <p class="text-sm">
            Enter a custom CPM value if you've negotiated a rate with the publisher. The default values are {{ publisher.cpm }} for
            {{ publisher.name }}.
          </p>
          <ElFormItem :prop="'cpms[' + publishers.indexOf(publisher) + '].cpm'" :rules="[getValidationRulesForPublisher(publisher)]">
            <span slot="label">{{ publisher.name }} CPM </span>
            <ElInput v-model="form.cpms.find((cpm) => cpm.publisher_id === publisher.id).cpm" type="number" placeholder="Ex. 3.5" />
          </ElFormItem>
        </div>
      </div>
    </ElForm>
    <!-- end form -->
    <template slot="footer">
      <el-button class="font-semibold" @click="close">{{ $t('cancel') }}</el-button>
      <el-button class="font-semibold ml-0" :loading="loading" type="primary" @click="submitForm">Create client</el-button>
    </template>
  </ModalLayout>
</template>
<script>
import ModalLayout from './ModalLayout.vue'
import ClientsAPI from '@/api/user/clients'
import { mapGetters } from 'vuex'

export default {
  name: 'AddClient',
  components: { ModalLayout },
  props: {
    meta: {
      type: null,
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      user_type: 'user/type',
    }),
    isAgencyAdmin() {
      return this.user_type === 'agency'
    },
    isAgencyEmployee() {
      return ['agency-employee', 'employee'].includes(this.user_type)
    },
  },
  data() {
    return {
      pageLoading: false,
      loading: false,
      publishers: [],
      countries: [],
      industries: [],
      resources: {
        max_active_campaigns: 0,
        max_monthly_influencers: 0,
      },
      form: {
        name: '',
        email: '',
        max_active_campaigns: 0,
        max_monthly_influencers: 0,
        cpms: [],
        country: null,
        industry: null,
      },
      errors: {
        name: null,
        email: null,
        max_active_campaigns: null,
        max_monthly_influencers: null,
        country: null,
        industry: null,
      },
      rules: {
        name: [
          { required: true, message: 'Please enter the client name', trigger: 'blur' },
          { min: 3, message: 'The client name must have at least 3 characters', trigger: 'blur' },
          { max: 50, message: 'The client name must have at most 50 characters', trigger: 'blur' },
        ],
        email: [
          { required: true, message: 'Please enter the client email', trigger: 'blur' },
          { type: 'email', message: 'Please enter a valid email address', trigger: 'blur' },
        ],
        max_active_campaigns: [
          { required: true, message: 'Please enter the maximum number of active campaigns', trigger: 'blur' },
          { type: 'number', message: 'Please enter a valid number', trigger: 'blur' },
        ],
        max_monthly_influencers: [
          { required: true, message: 'Please enter the maximum number of monthly influencers', trigger: 'blur' },
          { type: 'number', message: 'Please enter a valid number', trigger: 'blur' },
        ],
        country: [
          { required: false, message: 'Please select a country', trigger: 'change' },
        ],
        industry: [
          { required: false, message: 'Please select an industry', trigger: 'change' },
        ],
      },
    }
  },
  created() {
    // Country and Industry required for Agency Admin or Agency Employee
    if (this.isAgencyAdmin || this.isAgencyEmployee) {
      this.rules.country[0].required = true
      this.rules.industry[0].required = true
    }
    this.init()
  },
  methods: {
    close() {
      this.$Modal.instance.isOpen = false
    },
    async init() {
      this.pageLoading = true
      try {
        const { data } = await this.axios.get('/api/user/accounts/resources')
        this.resources.max_active_campaigns = data.max_active_campaigns
        this.resources.max_monthly_influencers = data.max_monthly_influencers
        this.publishers = [{ id: 16840, name: 'Thematic Digital', cpm: 3.5 }]
        this.form.cpms = this.publishers.map((publisher) => ({ publisher_id: publisher.id, cpm: Number(publisher.cpm) }))
        
        // Fetch countries and industries if needed
        if (this.isAgencyAdmin || this.isAgencyEmployee) {
          await this.getCountriesAndIndustries()
        }
      } catch (err) {
        console.log(err)
      } finally {
        this.pageLoading = false
      }
    },
    async getCountriesAndIndustries() {
      try {
        const { data } = await ClientsAPI.getCountriesIndustries()
        if (data) {
          this.countries = data['countries']
          this.industries = data['industries']
        }
      } catch (err) {
        console.log(err)
      }
    },
    validateCpm(rule, value, callback, publisher) {
      const cpmValue = parseFloat(value)
      if (isNaN(cpmValue)) {
        return callback(new Error('This field is required and must be a number.'))
      }
      if (cpmValue < publisher.cpm) {
        return callback(new Error(`CPM must be greater than or equal to ${publisher.cpm}.`))
      }
      callback() // Validation passed
    },
    getValidationRulesForPublisher(publisher) {
      return {
        validator: (rule, value, callback) => {
          const cpmInfo = this.form.cpms.find((cpm) => cpm.publisher_id === publisher.id)
          if (cpmInfo) {
            this.validateCpm(rule, cpmInfo.cpm, callback, publisher)
          } else {
            callback() // Dacă nu există cpm, considerăm valid
          }
        },
        trigger: 'blur',
      }
    },
    submitForm() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return
        this.loading = true
        try {
          await ClientsAPI.create(this.form)
          this.$Modal.parent.init()
          this.close()
        } catch (err) {
          console.log(err)
        } finally {
          this.loading = false
        }
      })
    },
  },
}
</script>
